import {
  AddEquation,
  CustomBlending,
  DoubleSide,
  NodeMaterial,
  OneFactor,
  OneMinusSrcAlphaFactor,
  type Texture,
  type UniformNode,
  type Vector2,
} from "three/webgpu";
import * as TSL from "three/tsl";

// Node types in @types/three are deep generics that do not follow swizzles and mixed-type arithmetic through
// helper functions; the graph is checked where it matters, when three compiles it.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
const {
  abs, attribute, clamp, cos, dot, exp, float, floor, Fn, If, length, max, min, mix, select, sign, sin,
  smoothstep, sqrt, texture, varying, vec2, vec4,
} = TSL as N;

/**
 * One material for every 2D primitive - the TSL form of what was the UI's single WGSL pipeline.
 *
 * A unit quad is expanded per instance in the vertex stage. The fragment stage evaluates a signed distance
 * field per instance - rounded rect, capsule, triangle, arc, rhombus or any triangle - for antialiased
 * edges and borders, and switches sampling mode per instance. Instance layout: six vec4s, see
 * `QuadBatcher` (`FLOATS_PER_INSTANCE`).
 *
 * Written in TSL so it compiles to WGSL on WebGPU and to GLSL on three's WebGL 2 fallback alike.
 *
 * The clip is per instance, in the fragment stage, rather than a scissor per draw: three draws a mesh with
 * one scissor, so a clip change would otherwise cost a draw call and a render pass. Batches therefore
 * break only where the texture changes.
 */

export const MODE_SOLID = 0;
export const MODE_GLYPH = 1;
export const MODE_IMAGE = 2;
export const MODE_GRADIENT = 3;
export const MODE_CAPSULE = 4;
export const MODE_TRIANGLE = 5;
export const MODE_ARC = 6;
export const MODE_DIAMOND = 7;
export const MODE_RADIAL = 8;
export const MODE_CHECKER = 9;
export const MODE_GLOW = 10;
export const MODE_SHINE = 11;
export const MODE_POLY = 12;
/** `MODE_IMAGE` for a texture whose color is already multiplied by alpha - a rendered scene. */
export const MODE_IMAGE_PREMULTIPLIED = 13;

/** Instance attribute names, in buffer order. */
export const INSTANCE_ATTRIBUTES = ["iRect", "iColor", "iBorder", "iUv", "iParams", "iClip"] as const;

/** Signed distance past the edge a-b, positive outside a triangle wound `s` (+1 or -1). */
const edgeOut = (p: N, a: N, b: N, s: N): N => {
  const e = b.sub(a);
  return s.negate().mul(e.x.mul(p.y.sub(a.y)).sub(e.y.mul(p.x.sub(a.x)))).div(max(length(e), 1.0e-6));
};

/** Signed distance to the triangle a-b-c. Negative inside. */
const sdTriangle = (p: N, a: N, b: N, c: N): N => {
  const e0 = b.sub(a), v0 = p.sub(a);
  const e1 = c.sub(b), v1 = p.sub(b);
  const e2 = a.sub(c), v2 = p.sub(c);
  const pq0 = v0.sub(e0.mul(clamp(dot(v0, e0).div(dot(e0, e0)), 0.0, 1.0)));
  const pq1 = v1.sub(e1.mul(clamp(dot(v1, e1).div(dot(e1, e1)), 0.0, 1.0)));
  const pq2 = v2.sub(e2.mul(clamp(dot(v2, e2).div(dot(e2, e2)), 0.0, 1.0)));
  const s = sign(e0.x.mul(e2.y).sub(e0.y.mul(e2.x)));
  const d = min(
    min(
      vec2(dot(pq0, pq0), s.mul(v0.x.mul(e0.y).sub(v0.y.mul(e0.x)))),
      vec2(dot(pq1, pq1), s.mul(v1.x.mul(e1.y).sub(v1.y.mul(e1.x)))),
    ),
    vec2(dot(pq2, pq2), s.mul(v2.x.mul(e2.y).sub(v2.y.mul(e2.x)))),
  );
  return sqrt(d.x).negate().mul(sign(d.y));
};

/** Distance to the segment a-b, unstroked. */
const sdSegment = (p: N, a: N, b: N): N => {
  const pa = p.sub(a);
  const ba = b.sub(a);
  const h = clamp(dot(pa, ba).div(max(dot(ba, ba), 1.0e-6)), 0.0, 1.0);
  return length(pa.sub(ba.mul(h)));
};

/**
 * A pie slice from the origin: radius `r`, symmetric about +Y, `c` the (sin, cos) of its half-aperture.
 * Flat-capped, which is what a sweep wants.
 */
const sdPie = (p: N, c: N, r: N): N => {
  const q = vec2(abs(p.x), p.y);
  const l = length(q).sub(r);
  const m = length(q.sub(c.mul(clamp(dot(q, c), 0.0, r))));
  return max(l, m.mul(sign(c.y.mul(q.x).sub(c.x.mul(q.y)))));
};

/** A rhombus with its corners at the midpoints of a 2*half box - Inigo Quilez's exact form. */
const sdRhombus = (p: N, half: N): N => {
  const q = abs(p);
  const ndot = half.sub(q.mul(2.0)).x.mul(half.x).sub(half.sub(q.mul(2.0)).y.mul(half.y));
  const h = clamp(ndot.div(max(dot(half, half), 1.0e-6)), -1.0, 1.0);
  const d = length(q.sub(half.mul(0.5).mul(vec2(float(1.0).sub(h), float(1.0).add(h)))));
  return d.mul(sign(q.x.mul(half.y).add(q.y.mul(half.x)).sub(half.x.mul(half.y))));
};

/** Turn the sample point against `angle`, so the shape appears turned by it - clockwise on screen. */
const rotate2 = (p: N, angle: N): N => {
  const s = sin(angle);
  const c = cos(angle);
  return vec2(p.x.mul(c).add(p.y.mul(s)), p.y.mul(c).sub(p.x.mul(s)));
};

/** Signed distance to a rounded rectangle centered at the origin. */
const sdRoundRect = (p: N, half: N, radius: N): N => {
  const r = clamp(radius, 0.0, min(half.x, half.y));
  const q = abs(p).sub(half).add(r);
  return length(max(q, vec2(0.0))).add(min(max(q.x, q.y), 0.0)).sub(r);
};

/** `x mod 2` for whole numbers of either sign, as 0 or 1. */
const parity = (x: N): N => x.sub(floor(x.mul(0.5)).mul(2.0));

/** Bit `b` (0..2) of a small whole number held in a float. */
const bitOf = (flags: N, b: number): N => parity(floor(flags.div(2 ** b)));

export interface QuadMaterialUniforms {
  /** The surface size in logical px. */
  viewport: UniformNode<"vec2", Vector2>;
}

/** The quad material for one texture. Every batch drawing with `map` shares it. */
export function createQuadMaterial(map: Texture, uniforms: QuadMaterialUniforms): NodeMaterial {
  const rect: N = attribute("iRect", "vec4");
  const color: N = attribute("iColor", "vec4");
  const border: N = attribute("iBorder", "vec4");
  const uvRect: N = attribute("iUv", "vec4");
  const params: N = attribute("iParams", "vec4");
  const clip: N = attribute("iClip", "vec4");
  const corner: N = attribute("position", "vec3").xy;

  // Grow the quad by one logical pixel per side so the SDF has room to fade out.
  const size = rect.zw.add(2.0);
  const posPx = rect.xy.sub(1.0).add(corner.mul(size));
  const ndc = posPx.div(uniforms.viewport).mul(2.0).sub(1.0);

  const vPos: N = varying(posPx, "vQuadPos");
  const vLocal: N = varying(corner.sub(0.5).mul(size), "vQuadLocal");
  // UVs span the rect itself, not the padded quad: stretching the texture over the pad magnifies every
  // glyph by 12-50% and is what made small text look soft.
  const uvT = posPx.sub(rect.xy).div(max(rect.zw, vec2(1.0e-4)));
  const vUv: N = varying(mix(uvRect.xy, uvRect.zw, uvT), "vQuadUv");
  const vHalf: N = varying(rect.zw.mul(0.5), "vQuadHalf");

  const fragment = Fn(() => {
    const local = vLocal;
    const half = vHalf;
    const radius = params.x;
    const borderWidth = params.y;
    // Half-width of the antialiasing band, in logical px: 0.5 / dpr, so the fade is one device pixel.
    const aa = max(params.z, 1.0e-4);
    const mode = floor(params.w.add(0.5));

    // Sampled first and unconditionally: WGSL requires a texture sample in uniform control flow, and a
    // refused module is the one pipeline every quad on the screen is drawn with. Clamped to the instance's
    // own sub-rectangle so the pad ring cannot reach into a neighboring atlas slot.
    const uvLo = min(uvRect.xy, uvRect.zw);
    const uvHi = max(uvRect.xy, uvRect.zw);
    const texel = texture(map, clamp(vUv, uvLo, uvHi)).toVar("texel");

    const d = sdRoundRect(local, half, radius).toVar("dist");

    If(mode.equal(MODE_CAPSULE), () => {
      // `radius` is the half-thickness here, and is deliberately not clamped to the box.
      d.assign(sdSegment(local, uvRect.xy, uvRect.zw).sub(radius));
    })
      .ElseIf(mode.equal(MODE_DIAMOND), () => {
        d.assign(sdRhombus(rotate2(local, uvRect.y), half));
      })
      .ElseIf(mode.equal(MODE_TRIANGLE), () => {
        const dir = floor(uvRect.x.add(0.5));
        const a = vec2(half.x.negate(), half.y).toVar();
        const b = vec2(half.x, half.y).toVar();
        const c = vec2(0.0, half.y.negate()).toVar();
        If(dir.equal(1), () => {
          a.assign(vec2(half.x.negate(), half.y.negate()));
          b.assign(vec2(half.x, half.y.negate()));
          c.assign(vec2(0.0, half.y));
        })
          .ElseIf(dir.equal(2), () => {
            a.assign(vec2(half.x, half.y.negate()));
            b.assign(vec2(half.x, half.y));
            c.assign(vec2(half.x.negate(), 0.0));
          })
          .ElseIf(dir.equal(3), () => {
            a.assign(vec2(half.x.negate(), half.y.negate()));
            b.assign(vec2(half.x.negate(), half.y));
            c.assign(vec2(half.x, 0.0));
          });
        d.assign(sdTriangle(rotate2(local, uvRect.y), a, b, c));
      })
      .ElseIf(mode.equal(MODE_POLY), () => {
        const pa = uvRect.xy;
        const pb = uvRect.zw;
        const pc = border.xy;
        const flags = floor(border.z.add(0.5));
        const e = pb.sub(pa);
        const f = pc.sub(pa);
        const s = select(e.x.mul(f.y).sub(e.y.mul(f.x)).greaterThanEqual(0.0), float(1.0), float(-1.0));
        const edges = [edgeOut(local, pa, pb, s), edgeOut(local, pb, pc, s), edgeOut(local, pc, pa, s)];
        // A boundary edge fades over the AA band; a shared one is a hard cut at the line itself.
        d.assign(-1.0e6);
        edges.forEach((de, bit) => {
          const hard = select(de.greaterThan(0.0), float(1.0e6), float(-1.0e6));
          d.assign(max(d, select(bitOf(flags, bit).greaterThan(0.5), de, hard)));
        });
      })
      .ElseIf(mode.equal(MODE_ARC), () => {
        const a0 = uvRect.x;
        const a1 = uvRect.y;
        const r0 = uvRect.z;
        const r1 = uvRect.w;
        const mid = a0.add(a1).mul(0.5);
        const ap = abs(a1.sub(a0)).mul(0.5);
        // Into a maths frame (+y up), then rotate the sector's bisector onto +Y so the symmetric sdPie applies.
        const pm = vec2(local.x, local.y.negate());
        const sm = sin(mid);
        const cm = cos(mid);
        const p = vec2(pm.x.mul(cm).sub(pm.y.mul(sm)), pm.y.mul(cm).add(pm.x.mul(sm)));
        const ring = abs(length(p).sub(r0.add(r1).mul(0.5))).sub(r1.sub(r0).mul(0.5));
        const wedge = max(sdPie(p, vec2(sin(ap), cos(ap)), r1), r0.sub(length(p)));
        // A full turn degenerates the wedge cut and leaves only the ring. Intersected with the quad's own
        // rounded box, which turns a circular sector into a cooldown sweep over a rounded icon.
        d.assign(max(select(ap.greaterThanEqual(3.14159265), ring, wedge), sdRoundRect(local, half, radius)));
      });

    // Coverage of the silhouette, and of the interior inside the border ring.
    const outerCov = float(1.0).sub(smoothstep(aa.negate(), aa, d));
    const innerCov = float(1.0).sub(smoothstep(aa.negate(), aa, d.add(borderWidth)));

    // The clip, per fragment: a quad straddling a clip edge is cut there, as a scissor would cut it.
    const inside = vPos.x
      .greaterThanEqual(clip.x)
      .and(vPos.x.lessThan(clip.z))
      .and(vPos.y.greaterThanEqual(clip.y))
      .and(vPos.y.lessThan(clip.w));
    const clipCov = select(inside, float(1.0), float(0.0));

    const result = vec4(0.0).toVar("result");

    If(mode.equal(MODE_GLOW), () => {
      const spread = max(uvRect.x, 1.0e-3);
      const shape = max(half.sub(spread), vec2(0.0));
      const dg = sdRoundRect(local, shape, radius);
      // Exponential, as light is, and brought to exactly nothing at the quad's edge so no seam shows.
      const fall = exp(max(dg, 0.0).negate().div(spread.mul(0.32))).mul(float(1.0).sub(smoothstep(spread.mul(0.7), spread, dg)));
      const ga = color.a.mul(fall);
      result.assign(vec4(color.rgb.mul(ga), ga));
    }).Else(() => {
      const fill = vec4(color).toVar("fill");
      If(mode.equal(MODE_GLYPH), () => {
        // The atlas stores coverage in alpha; the instance color inks it.
        fill.assign(vec4(color.rgb, color.a.mul(texel.a)));
      })
        .ElseIf(mode.equal(MODE_IMAGE), () => {
          fill.assign(vec4(texel.rgb.mul(color.rgb), texel.a.mul(color.a)));
        })
        .ElseIf(mode.equal(MODE_IMAGE_PREMULTIPLIED), () => {
          fill.assign(vec4(texel.rgb.div(max(texel.a, 1.0e-4)).mul(color.rgb), texel.a.mul(color.a)));
        })
        .ElseIf(mode.equal(MODE_GRADIENT), () => {
          // Position within the unpadded rect, 0..1, from `local` rather than the padded uv.
          const q = local.div(max(half.mul(2.0), vec2(1.0e-4))).add(0.5);
          const a = uvRect.xy;
          const ab = uvRect.zw.sub(a);
          const t = clamp(dot(q.sub(a), ab).div(max(dot(ab, ab), 1.0e-6)), 0.0, 1.0);
          fill.assign(mix(color, border, t));
        })
        .ElseIf(mode.equal(MODE_CHECKER), () => {
          const q = floor(local.add(half).div(max(uvRect.x, 1.0e-4)));
          const lit = parity(q.x.add(q.y).add(floor(uvRect.y.add(0.5)))).lessThan(0.5);
          fill.assign(vec4(color.rgb, select(lit, color.a, float(0.0))));
        })
        .ElseIf(mode.equal(MODE_SHINE), () => {
          const q = local.div(max(half.mul(2.0), vec2(1.0e-4))).add(0.5);
          const s = q.x.add(q.y).mul(0.5);
          const band = float(1.0).sub(smoothstep(0.0, max(uvRect.y, 1.0e-3), abs(s.sub(uvRect.x))));
          fill.assign(vec4(color.rgb, color.a.mul(band).mul(band)));
        })
        .ElseIf(mode.equal(MODE_RADIAL), () => {
          const q = local.div(max(half.mul(2.0), vec2(1.0e-4))).add(0.5);
          // Normalized by the radii separately: an ellipse filling the rect.
          const delta = q.sub(uvRect.xy).div(max(uvRect.zw, vec2(1.0e-4)));
          fill.assign(mix(color, border, clamp(length(delta), 0.0, 1.0)));
        });

      // The border is the ring between the silhouette and the interior, so the two are disjoint and add
      // in premultiplied space.
      const ringCov = max(outerCov.sub(innerCov), 0.0);
      const fillA = fill.a.mul(innerCov);
      const borderA = select(borderWidth.greaterThan(0.0), border.a, float(0.0)).mul(ringCov);
      result.assign(vec4(fill.rgb.mul(fillA).add(border.rgb.mul(borderA)), fillA.add(borderA)));
    });

    return result.mul(clipCov);
  });

  const material = new NodeMaterial();
  material.name = "webwin.quad";
  material.vertexNode = vec4(ndc.x, ndc.y.negate(), 0.0, 1.0);
  material.fragmentNode = fragment();
  material.transparent = true;
  material.depthTest = false;
  material.depthWrite = false;
  material.side = DoubleSide;
  // Premultiplied source over the destination.
  material.blending = CustomBlending;
  material.blendEquation = material.blendEquationAlpha = AddEquation;
  material.blendSrc = material.blendSrcAlpha = OneFactor;
  material.blendDst = material.blendDstAlpha = OneMinusSrcAlphaFactor;
  return material;
}
